import { useParams, Link } from 'react-router-dom';
import personalTrainersData from '@/data/personalTrainers.json';
import physiotherapistsData from '@/data/physiotherapists.json';
import type { Coach, Physiotherapist } from '@/data/types';
import { WHATSAPP_NUMBER } from '@/data/constants';
import { WhatsAppIcon } from '@/components/icons';

type ProfilePerson = (Coach | Physiotherapist) & {
  specialty?: string;
  specialization?: string;
  services?: string[];
};

const trainers = personalTrainersData as Coach[];
const physios = physiotherapistsData as Physiotherapist[];

function getSpecialty(person: ProfilePerson): string {
  return person.specialty ?? person.specialization ?? '';
}

function getServices(person: ProfilePerson): string[] {
  if ('services' in person && person.services) return person.services;
  if ('specialization' in person && person.specialization) {
    return person.specialization.split('·').map((s) => s.trim());
  }
  return [];
}

export default function CoachProfilePage() {
  const { slug } = useParams<{ slug: string }>();

  const person: ProfilePerson | undefined =
    (trainers.find((t) => t.slug === slug) as ProfilePerson | undefined) ??
    (physios.find((p) => p.slug === slug) as ProfilePerson | undefined);

  if (!person) {
    return (
      <div className="min-h-screen bg-[#111111] flex flex-col items-center justify-center text-center px-4">
        <p className="text-5xl font-bold text-white mb-4">404</p>
        <p className="text-gray-400 mb-8">Trainer not found.</p>
        <Link to="/" className="text-[#14B8A6] hover:underline text-sm">← Back to Hub30</Link>
      </div>
    );
  }

  const specialty = getSpecialty(person);
  const services = getServices(person);
  const bookMessage = encodeURIComponent(
    `Hi HUB30! I'd like to book a session with ${person.name}.`
  );
  const waNumber = person.socials?.whatsapp ?? WHATSAPP_NUMBER;
  const bookUrl = `https://wa.me/${waNumber}?text=${bookMessage}`;

  return (
    <div className="min-h-screen bg-[#111111] text-white">

      {/* Back bar */}
      <div className="sticky top-0 z-50 bg-[#111111]/90 backdrop-blur border-b border-white/[0.06] px-4 py-3">
        <div className="max-w-5xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white text-sm transition-colors"
          >
            <i className="ri-arrow-left-line" />
            Hub30
          </Link>
        </div>
      </div>

      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#14B8A6]/8 via-transparent to-[#111111]" />
        <div className="max-w-5xl mx-auto px-4 py-14 lg:py-20 relative z-10">
          <div className="flex flex-col sm:flex-row items-center sm:items-end gap-8">
            <div className="relative flex-shrink-0">
              <img
                src={person.image}
                alt={person.name}
                className="w-40 h-40 sm:w-52 sm:h-52 rounded-3xl object-cover object-top border-2 border-[#14B8A6]/40 shadow-[0_0_60px_rgba(20,184,166,0.2)]"
              />
            </div>
            <div className="text-center sm:text-left pb-2">
              {person.experience && (
                <span className="inline-block text-xs font-semibold text-[#14B8A6] bg-[#14B8A6]/10 border border-[#14B8A6]/20 px-3 py-1 rounded-full mb-3">
                  {person.experience} experience
                </span>
              )}
              <h1 className="text-4xl sm:text-5xl font-bold mb-2">{person.name}</h1>
              <p className="text-[#14B8A6] font-medium text-lg mb-5">{specialty}</p>
              <div className="flex flex-wrap gap-3 justify-center sm:justify-start">
                <a
                  href={bookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold px-6 py-3 rounded-full transition-all duration-300 hover:shadow-[0_0_20px_rgba(37,211,102,0.35)] text-sm"
                >
                  <WhatsAppIcon className="text-base" />
                  Book a Session
                </a>
                {person.socials?.instagram && (
                  <a
                    href={
                      person.socials.instagram.startsWith('http')
                        ? person.socials.instagram
                        : `https://instagram.com/${person.socials.instagram.replace('@', '')}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 border border-white/15 hover:border-[#E1306C]/50 text-gray-300 hover:text-white font-semibold px-6 py-3 rounded-full transition-all duration-300 text-sm"
                  >
                    <i className="ri-instagram-line text-base" />
                    Instagram
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 pb-24 space-y-14">

        {/* About */}
        {person.fullBio && (
          <section>
            <SectionTitle>About</SectionTitle>
            <p className="text-gray-300 leading-relaxed text-base lg:text-lg max-w-3xl">{person.fullBio}</p>
          </section>
        )}

        {/* Specialties */}
        {services.length > 0 && (
          <section>
            <SectionTitle>Specialties</SectionTitle>
            <div className="flex flex-wrap gap-2">
              {services.map((s) => (
                <span
                  key={s}
                  className="text-sm text-[#14B8A6] bg-[#14B8A6]/10 border border-[#14B8A6]/25 px-4 py-1.5 rounded-full font-medium"
                >
                  {s}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* Certifications */}
        {person.certifications && person.certifications.length > 0 && (
          <section>
            <SectionTitle>Certifications</SectionTitle>
            <ul className="space-y-2.5">
              {person.certifications.map((cert) => (
                <li key={cert} className="flex items-center gap-3 text-gray-300 text-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#14B8A6] flex-shrink-0" />
                  {cert}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Gallery */}
        {person.gallery && person.gallery.length > 0 && (
          <section>
            <SectionTitle>Gallery</SectionTitle>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {person.gallery.map((src, i) => (
                <div key={i} className="aspect-square rounded-2xl overflow-hidden bg-[#1a1a1a] border border-white/[0.06]">
                  <img src={src} alt={`${person.name} ${i + 1}`} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Socials footer CTA */}
        <section className="border-t border-white/[0.06] pt-12 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <p className="text-white font-bold text-xl mb-1">Ready to train with {person.name.split(' ')[0]}?</p>
            <p className="text-gray-400 text-sm">Reach out directly to book your first session.</p>
          </div>
          <a
            href={bookUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold px-7 py-4 rounded-full transition-all duration-300 hover:shadow-[0_0_24px_rgba(37,211,102,0.35)] text-sm flex-shrink-0"
          >
            <WhatsAppIcon className="text-lg" />
            Book a Session
          </a>
        </section>
      </div>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-xl font-bold text-white mb-5 flex items-center gap-3">
      <span className="w-4 h-px bg-[#14B8A6]" />
      {children}
    </h2>
  );
}
